import { useCallback, useEffect, useState } from 'react';
import { onSynced } from '@/lib/syncSignal';
import { classifyDownload, isRefusedSign, toFetchError } from '@/services/fetchPhoto';

export const RETRY_DELAYS_MS = [2_000, 10_000, 60_000];

type RetryOn = 'sync' | 'timer';

function retryOn(error: unknown): RetryOn | undefined {
  const err = toFetchError(error);
  if (isRefusedSign(err)) return 'sync';
  switch (classifyDownload(err.status)) {
    case 'offline':
    case 'signed_out':
      return 'sync';
    case 'transient':
    case 'rate_limited':
      return 'timer';
    case 'permanent':
      return undefined;
  }
}

interface RetryState {
  hash: string | undefined;
  attempt: number;
  on?: RetryOn;
}

/**
 * Counts retries of one download; an effect keyed on `attempt` downloads again. `failed` schedules
 * the next: a refused sign, offline or signed out after the next successful sync, a 5xx or
 * timeout after `RETRY_DELAYS_MS`. Another refusal, or `RETRY_DELAYS_MS.length` retries, ends it.
 */
export function useDownloadRetry(hash: string | undefined): {
  attempt: number;
  failed: (error: unknown) => void;
} {
  const [state, setState] = useState<RetryState>({ hash, attempt: 0 });
  if (state.hash !== hash) setState({ hash, attempt: 0 });
  const { attempt, on } = state.hash === hash ? state : { attempt: 0, on: undefined };

  useEffect(() => {
    if (!on || attempt >= RETRY_DELAYS_MS.length) return;
    const next = () =>
      setState((s) =>
        s.hash === hash && s.attempt === attempt ? { hash, attempt: attempt + 1 } : s,
      );
    if (on === 'sync') return onSynced(next);
    const id = setTimeout(next, RETRY_DELAYS_MS[attempt]);
    return () => clearTimeout(id);
  }, [hash, attempt, on]);

  const failed = useCallback(
    (error: unknown) => {
      const retry = retryOn(error);
      setState((s) => (s.hash === hash && s.attempt === attempt ? { ...s, on: retry } : s));
    },
    [hash, attempt],
  );
  return { attempt, failed };
}
